import type { CarParkingLocationKey } from '@/lib/pages/carParkingLinks'

/** WordPress / import legacy slugs mapped to Payload + frontend location slugs. */
export const LEGACY_TO_CANONICAL_LOCATION_SLUG: Record<string, string> = {
  'th-neukolln': 'berlin-neukoelln',
  'th-neukoelln': 'berlin-neukoelln',
  'tannhaus-berlin-neuklln': 'berlin-neukoelln',
  'frankfurter-tor': 'berlin-frankfurter-tor',
  adlershof: 'berlin-adlershof',
  k115: 'cologne-k115',
  koeln: 'cologne-k115',
  'riedberg-2': 'frankfurt-riedberg',
  riedberg: 'frankfurt-riedberg',
  'berlin-mitte-wedding-2': 'berlin-mitte-wedding',
  'mitte-wedding-2': 'berlin-mitte-wedding',
  'berlin-mitte-wedding-m': 'berlin-mitte-wedding',
  tuebingen: 'tu3',
  'tubingen-2': 'tu3',
  tubingen: 'tu3',
}

/** Legacy import slugs that resolve to a different Payload `locations.slug`. */
export const LEGACY_TO_PAYLOAD_LOCATION_SLUG: Record<string, string> = {
  tuebingen: 'tu3',
  'tubingen-2': 'tu3',
  tubingen: 'tu3',
}

/**
 * Legacy WordPress location slugs that should not be imported as SEO sources
 * (parking / garage sub-pages with duplicate or irrelevant meta).
 */
export const SKIPPED_LEGACY_LOCATION_SLUGS = new Set([
  'riedberg-car-parking-2',
  'riedberg-parkplaetze-2',
  'cologne-car-parking',
  'koeln-tiefgarage',
])

/**
 * Parking / garage legacy URLs use these location keys both to resolve the
 * associated Payload location and to preselect the new parking page.
 */
export const SKIPPED_LOCATION_REDIRECT_TARGETS: Record<string, CarParkingLocationKey> = {
  'riedberg-car-parking-2': 'frankfurt-riedberg',
  'riedberg-parkplaetze-2': 'frankfurt-riedberg',
  'cologne-car-parking': 'cologne-k115',
  'koeln-tiefgarage': 'cologne-k115',
}

/**
 * When multiple legacy URLs map to the same Payload location + locale, these
 * secondary sources are only used if the primary source is missing/unscrapable.
 */
export const SECONDARY_LEGACY_LOCATION_SLUGS = new Set(['berlin-mitte-wedding-m'])

export function normalizeLegacyLocationSlug(slug: string): string {
  return LEGACY_TO_CANONICAL_LOCATION_SLUG[slug] ?? slug
}

/** Frontend + SEO canonical location slug for nested city routes. */
export function getCanonicalLocationSlug(slug: string): string {
  return normalizeLegacyLocationSlug(slug.trim())
}

export function isSkippedLegacyLocationSlug(slug: string): boolean {
  return SKIPPED_LEGACY_LOCATION_SLUGS.has(slug)
}

export function getSkippedLocationRedirectTarget(
  slug: string,
): CarParkingLocationKey | undefined {
  return SKIPPED_LOCATION_REDIRECT_TARGETS[slug]
}

/**
 * Legacy slug used when resolving a Payload location for a redirect destination.
 * Parking sub-pages resolve through their associated location; others use the original slug.
 */
export function getLocationRedirectLookupSlug(legacySlug: string): string {
  return SKIPPED_LOCATION_REDIRECT_TARGETS[legacySlug] ?? legacySlug
}

export function isSecondaryLegacyLocationSlug(slug: string): boolean {
  return SECONDARY_LEGACY_LOCATION_SLUGS.has(slug)
}

export function getPayloadLocationSlugCandidates(legacyLocationSlug: string): string[] {
  const canonical = normalizeLegacyLocationSlug(legacyLocationSlug)
  const payloadSlug =
    LEGACY_TO_PAYLOAD_LOCATION_SLUG[legacyLocationSlug] ??
    LEGACY_TO_PAYLOAD_LOCATION_SLUG[canonical] ??
    canonical
  const candidates = [payloadSlug, canonical, legacyLocationSlug]

  for (const [legacy, mapped] of Object.entries(LEGACY_TO_CANONICAL_LOCATION_SLUG)) {
    if (mapped === canonical) {
      candidates.push(legacy)
    }
  }

  for (const [legacy, mapped] of Object.entries(LEGACY_TO_PAYLOAD_LOCATION_SLUG)) {
    if (mapped === payloadSlug) {
      candidates.push(legacy)
    }
  }

  return [...new Set(candidates)]
}
